import { useState, useEffect } from 'react'
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import {
  Container,
  Paper,
  Stack,
  Group,
  Button,
  TextInput,
  Textarea,
  Select,
  Badge,
  Text,
  Loader,
  Breadcrumbs,
  Anchor,
  Divider,
  Checkbox,
} from '@mantine/core'
import { IconArrowLeft, IconDeviceFloppy } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { ResponsiveTitle } from '../../components/ResponsiveTitle/ResponsiveTitle'
import { useGuests } from '../../hooks/useGuests'
import { AdminNavbar } from '../../components/AdminNavbar/AdminNavbar'
import { AdditionalGuestForm } from '../../components/AdditionalGuestForm/AdditionalGuestForm'
import { ChangelogTimeline } from '../../components/ChangelogTimeline/ChangelogTimeline'
import { isAdminAuthenticated } from '../../lib/auth'
import type { GuestWithDetails, RsvpStatus } from '../../lib/types'
import styles from './guest.$id.module.css'

export const Route = createFileRoute('/admin/guest/$id')({
  component: GuestDetail,
})

function GuestDetail() {
  const navigate = useNavigate()
  const params = useParams({ from: '/admin/guest/$id' })
  const { getGuestById, updateGuest, error: guestError } = useGuests()

  const [guest, setGuest] = useState<GuestWithDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Form state
  const [primaryName, setPrimaryName] = useState('')
  const [contactInfo, setContactInfo] = useState('')
  const [email, setEmail] = useState('')
  const [rsvpStatus, setRsvpStatus] = useState<'unknown' | 'coming' | 'not_coming'>('unknown')
  const [dietaryVegan, setDietaryVegan] = useState(false)
  const [dietaryGlutenFree, setDietaryGlutenFree] = useState(false)
  const [dietaryLactoseFree, setDietaryLactoseFree] = useState(false)
  const [dietaryAllergies, setDietaryAllergies] = useState('')
  const [dietaryMisc, setDietaryMisc] = useState('')
  const [comments, setComments] = useState('')

  useEffect(() => {
    // Check auth
    if (!isAdminAuthenticated()) {
      navigate({ to: '/admin/login' })
      return
    }

    // Fetch guest data
    const loadGuest = async () => {
      setLoading(true)
      const guestData = await getGuestById(params.id)
      if (guestData) {
        setGuest(guestData)
        setPrimaryName(guestData.primary_name)
        setContactInfo(guestData.contact_info || '')
        setEmail(guestData.email || '')
        setRsvpStatus(guestData.rsvp_status as RsvpStatus)
        setDietaryVegan(guestData.dietary_vegan || false)
        setDietaryGlutenFree(guestData.dietary_gluten_free || false)
        setDietaryLactoseFree(guestData.dietary_lactose_free || false)
        setDietaryAllergies(guestData.dietary_allergies || '')
        setDietaryMisc(guestData.dietary_misc || '')
        setComments(guestData.comments || '')
      } else {
        notifications.show({
          title: 'Fehler',
          message: 'Gast nicht gefunden',
          color: 'red',
        })
        navigate({
          to: '/admin',
          search: {
            page: 1,
            pageSize: 5,
            sortField: 'name',
            sortDirection: 'asc',
            nameFilter: '',
            statusFilter: '',
          },
        })
      }
      setLoading(false)
    }

    loadGuest()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id])

  const handleSave = async () => {
    if (!guest) return

    setSaving(true)
    const success = await updateGuest(guest.id, {
      primary_name: primaryName,
      contact_info: contactInfo || null,
      email: email || null,
      rsvp_status: rsvpStatus,
      dietary_vegan: dietaryVegan,
      dietary_gluten_free: dietaryGlutenFree,
      dietary_lactose_free: dietaryLactoseFree,
      dietary_allergies: dietaryAllergies || null,
      dietary_misc: dietaryMisc || null,
      comments: comments || null,
    })

    if (success) {
      notifications.show({
        title: 'Erfolgreich gespeichert',
        message: 'Änderungen wurden gespeichert.',
        color: 'green',
      })

      // Reload guest data to get updated changelog
      const updatedGuest = await getGuestById(guest.id)
      if (updatedGuest) {
        setGuest(updatedGuest)
      }
    } else {
      notifications.show({
        title: 'Fehler',
        message: guestError || 'Änderungen konnten nicht gespeichert werden.',
        color: 'red',
        autoClose: 10000,
      })
    }

    setSaving(false)
  }

  const handleGuestRefresh = async () => {
    if (!guest) return
    const updatedGuest = await getGuestById(guest.id)
    if (updatedGuest) {
      setGuest(updatedGuest)
    }
  }

  if (loading) {
    return (
      <div className={styles.adminLayout}>
        <AdminNavbar />
        <Container size="xl" py="xl">
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Lädt...</Text>
          </Stack>
        </Container>
      </div>
    )
  }

  if (!guest) {
    return null
  }

  const breadcrumbItems = [
    { title: 'Admin-Dashboard', href: '/admin' },
    { title: guest.primary_name, href: `/admin/guest/${guest.id}` },
  ].map((item, index) => (
    <Anchor key={index} onClick={() => navigate({ to: item.href })} className={styles.breadcrumb}>
      {item.title}
    </Anchor>
  ))

  return (
    <div className={styles.adminLayout}>
      <AdminNavbar />
      <Container size="xl" py="xl">
        <Stack gap="xl">
          <Group justify="space-between" align="center">
            <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={18} />}
              onClick={() =>
                navigate({
                  to: '/admin',
                  search: {
                    page: 1,
                    pageSize: 5,
                    sortField: 'name',
                    sortDirection: 'asc',
                    nameFilter: '',
                    statusFilter: '',
                  },
                })
              }
            >
              Zurück zur Liste
            </Button>
          </Group>

          <Paper p="xl" shadow="sm" className={styles.detailCard}>
            <Stack gap="xl">
              <Group justify="space-between" align="center">
                <ResponsiveTitle order={2} className={styles.title}>
                  Gast-Details
                </ResponsiveTitle>
                <Badge size="lg" variant="light">
                  PIN: {guest.pin}
                </Badge>
              </Group>

              <Stack gap="md">
                <TextInput
                  label="Name"
                  value={primaryName}
                  onChange={(e) => setPrimaryName(e.currentTarget.value)}
                  required
                />

                <TextInput
                  label="Kontakt"
                  placeholder="Telefon (optional)"
                  value={contactInfo}
                  onChange={(e) => setContactInfo(e.currentTarget.value)}
                />

                <TextInput
                  label="E-Mail"
                  placeholder="E-Mail-Adresse (optional)"
                  description="Vom Gast angegeben für Updates"
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                  type="email"
                />

                <Select
                  label="RSVP-Status"
                  value={rsvpStatus}
                  onChange={(value) => setRsvpStatus(value as typeof rsvpStatus)}
                  data={[
                    { value: 'unknown', label: 'Unbekannt' },
                    { value: 'coming', label: 'Kommt' },
                    { value: 'not_coming', label: 'Kommt nicht' },
                  ]}
                />

                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Ernährungseinschränkungen
                  </Text>

                  <Checkbox
                    label="Vegan"
                    checked={dietaryVegan}
                    onChange={(e) => setDietaryVegan(e.currentTarget.checked)}
                  />

                  <Checkbox
                    label="Glutenfrei"
                    checked={dietaryGlutenFree}
                    onChange={(e) => setDietaryGlutenFree(e.currentTarget.checked)}
                  />

                  <Checkbox
                    label="Laktosefrei"
                    checked={dietaryLactoseFree}
                    onChange={(e) => setDietaryLactoseFree(e.currentTarget.checked)}
                  />

                  <TextInput
                    label="Allergien"
                    placeholder="z.B. Nüsse, Meeresfrüchte"
                    value={dietaryAllergies}
                    onChange={(e) => setDietaryAllergies(e.currentTarget.value)}
                  />

                  <TextInput
                    label="Sonstiges"
                    placeholder="Weitere Ernährungsanforderungen"
                    value={dietaryMisc}
                    onChange={(e) => setDietaryMisc(e.currentTarget.value)}
                  />
                </Stack>

                <Textarea
                  label="Kommentare"
                  placeholder="Zusätzliche Notizen oder Fragen"
                  value={comments}
                  onChange={(e) => setComments(e.currentTarget.value)}
                  rows={4}
                />

                <Group justify="flex-end">
                  <Button
                    leftSection={<IconDeviceFloppy size={18} />}
                    onClick={handleSave}
                    loading={saving}
                    className={styles.saveButton}
                  >
                    Änderungen speichern
                  </Button>
                </Group>
              </Stack>
            </Stack>
          </Paper>

          <Divider />

          {/* Additional Guests Section */}
          <Paper p="xl" shadow="sm" className={styles.detailCard}>
            <AdditionalGuestForm guestId={guest.id} onUpdate={handleGuestRefresh} />
          </Paper>

          <Divider />

          {/* Changelog Section */}
          <Paper p="xl" shadow="sm" className={styles.detailCard}>
            <ChangelogTimeline changelog={guest.changelog || []} />
          </Paper>
        </Stack>
      </Container>
    </div>
  )
}
